export default {
  // Shared fallback for INSUFFICIENT_BPS (buy/offer/counter) when the response carries no BE message.
  sellerShareShortfall: 'The seller no longer holds enough of this entry for this transaction.',
  fees: {
    loadError: 'We couldn’t load current fees. Please try again.',
    retry: 'Try again',
  },
  termsDisclaimer:
    'By using the Entry Marketplace you agree to the Marketplace <link>Terms of Use</link>',
  concentrationCapN:
    'You already hold {{held}} active entries in this contest. The marketplace limit is {{cap}}.',
  // FE-owned copy for the server MARKETPLACE_FROZEN error — the pre-deadline pick-lock freeze (the
  // live-games settlement pause is a separate code that renders its own backend message). During the
  // this copy is used by accept / buy-now paths. Undated: no clock time in paused copy.
  marketplacePaused:
    'Accepting is paused while picks lock. Try again when the marketplace reopens.',
  // FE-owned copy for MARKETPLACE_CONTEST_CLOSED (a non-active contest) on the tradeable-gated
  // create / edit / offer paths and on settlement. No reopen time — the contest is closed, not paused.
  marketplaceContestClosed: "This contest's marketplace is closed.",
  // Proactive paused banner, keyed by the closed reason. Frozen and game-window reasons carry a
  // resume line; no_upcoming_slate and not_enabled are single-line primaries.
  pausedBanner: {
    // frozen = the pre-deadline freeze while picks lock (reopens at the pick deadline); distinct
    // from the game window below, which begins 90 minutes before kickoff and ends on final.
    frozen: 'Trading on Marketplace is paused while picks lock.',
    pausedLiveGame: 'Trading on Marketplace is paused before and while games are live.',
    noUpcomingSlate: 'Trading on Marketplace is paused until the next slate is scheduled.',
    notEnabled: 'Trading on Marketplace is disabled for this contest.',
    contestClosed: 'Trading on Marketplace is closed for this contest.',
    // Neutral fallback for an unrecognized closed reason — never a specific claim (e.g. games live).
    generic: 'Trading on Marketplace is paused.',
    // Strip-only resume lines. Event-based, never a clock time (a timestamp would over-promise): the
    // Freeze lifts at the pick deadline; the game-window pause lifts once tonight's games are final.
    resumesAtDeadline: 'Resumes at the pick deadline.',
    resumesAfterGames: "Resumes after tonight's games end.",
  },
  // Stands in for an identity whose handle ships the 'unknown' sentinel (identity_enrichment miss).
  unknownHandle: 'Unknown',
  pickDisplay: {
    availableTeamsTitle: 'Available teams',
    noneAvailable: 'No teams available',
    showTeamPicks: 'Show team picks',
    teamsUnavailable: 'Team availability isn’t available right now.',
    filterByTeams: 'Filter by teams',
    filterByTeamsCount: 'Filter by teams ({{count}})',
    filterAvailable: 'Available',
    filterPicked: 'Picked',
    filterRemoveTeam: 'Remove {{team}} from {{filter}} filter',
    filterClose: 'Close filter',
    filterSearchPlaceholder: 'Search teams',
    filterNoMatches: 'No teams match',
    filterAtCap: 'You can filter on up to {{count}} teams at once',
    filterClear: 'Clear',
    filterApply: 'Apply',
    entriesFound__hasPlural: {
      one: '{{count}} entry found',
      other: '{{count}} entries found',
    },
  },
  lobby: {
    title: 'Entry marketplace',
    subtitle: 'Buy and sell entries in Survivor contests',
    close: 'Close marketplace',
    tabs: {
      buyEntries: 'Buy entries',
      myEntries: 'My entries',
      offers: 'Offers',
      partners: 'Partners',
      settings: 'Settings',
    },
    contestsHeading: 'Survivor Contests',
    contestsEmpty: 'No contests have entries for sale right now.',
  },
  recentSales: {
    label: 'Recently sold entries',
    sold: 'Sold',
    saleLabel: '{{seller}} sold {{share}} for {{price}} in {{contest}}, {{age}}',
    saleLabelNoContest: '{{seller}} sold {{share}} for {{price}}, {{age}}',
    saleLabelWithPickControl: '{{sale}}, pick control transferred',
    pickControlTransferred: 'Pick control transferred',
    age: {
      now: 'now',
      minutes: '{{count}}m ago',
      hours: '{{count}}h ago',
      days: '{{count}}d ago',
    },
  },
  partnerOffers: {
    tabs: {
      offers: 'Offers',
      keys: 'API keys',
    },
    draftsHeading: 'Drafts',
    draftsEmpty: 'No drafts to submit.',
    submitAll: 'Submit all',
    contestFallback: {
      direct: 'Direct offer',
      listing: 'Listing offer',
    },
    submitProgress: 'Submitting {{done}} of {{total}}…',
    submitError: 'We couldn’t submit your offers. Please try again.',
    submitStopped: 'Submit all stopped after {{done}} of {{total}} offers. Please try again.',
    submitStoppedReason: 'Submit all stopped after {{done}} of {{total}} offers. {{reason}}',
    submitRefused: {
      location: 'Submitting offers isn’t available in your location.',
      blocked:
        'Your account can’t use the marketplace. If you think this is an error, please contact support.',
      age: 'You don’t meet the age requirement to trade on the marketplace.',
      tradeUnavailable: 'Offers can’t be submitted right now.',
      paused: 'Submitting is paused while picks lock. Try again when the marketplace reopens.',
      accountNotActive: 'Your partner account isn’t active, so offers can’t be submitted.',
    },
    totalExposure: 'Total exposure',
    mayNotFit: 'May not fit',
    shareholderUnavailable: 'Shareholder unavailable',
    viewEntry: 'View entry details for {{contest}}',
    viewEntryShareholder: 'View entry details for {{contest}}, held by {{shareholder}}',
    columns: {
      contest: 'Contest Name',
      shareholder: 'Shareholder',
      share: 'Share (%)',
      bidPrice: 'Bid Price ($)',
      totalWithFees: 'Total with Fees ($)',
      expires: 'Expires',
      cancel: 'Cancel',
    },
    cancel: 'Cancel',
    cancelError: 'We couldn’t cancel that draft. Please try again.',
    termsChanged: 'Terms changed',
    confirm: 'Confirm',
    replacesCurrentOffer: 'Submitting replaces your current offer for this listing',
    noCurrentOffer: 'No current offer for this listing',
    replacesCurrentOfferShareholder:
      'Submitting replaces your current direct offer to {{shareholder}}',
    noCurrentOfferShareholder: 'No current direct offer to {{shareholder}}',
    currentOfferTerms: '{{label}}: {{percent}}% for {{amount}}',
    outcome: {
      submitted: 'Submitted',
      notSubmitted: 'Not submitted',
    },
    submitReason: {
      revisionStale: 'Terms changed — review and resubmit.',
      draftNotFound: 'This draft is no longer available.',
      targetNotFound: 'This listing is no longer available.',
      targetNotLive: 'This listing is no longer active.',
      offerExceedsCash: 'The bid plus fee is more than your eligible cash.',
      offerExceedsAccountCeiling:
        'This offer is above the limit set on your account. Lower it to place it.',
      offerCapLimitExceeded: 'Over your offer cap — cancel another offer.',
      maxOpenOffersReached:
        'You’ve reached the most open offers you can hold at once. Cancel one to place this.',
      balanceUnavailable: 'Your balance is temporarily unavailable. Please try again shortly.',
      tradeUnavailable: 'This offer isn’t available right now.',
      // One neutral line for both entry-limit refusals (CONCENTRATION_CAP_EXCEEDED and
      // MAX_ENTRIES_PER_USER) — different bounds on the same fact, same remedy; no cap number per item.
      entryCapUnknown: 'You’ve reached the limit of entries you can hold in this contest.',
      selfOfferNotAllowed: 'You can’t make an offer on your own entry.',
      targetInsufficientShare:
        'The shareholder no longer holds enough of this entry to fill your offer.',
      groupEntryTradeRestricted:
        'Group entries can only be traded directly between current shareholders.',
      shareExceedsListing: 'You can’t request more of the entry than this listing offers.',
      shareTooSmallToPrice:
        'Your requested share is too small to price at this listing’s rate. Increase your share.',
      offerExpirationOutOfRange:
        'Set the offer expiration in the future and before the listing or picks lock.',
      marketplaceNotEnabled: 'This contest isn’t available for marketplace trading.',
      belowMinShare: 'Your requested share is below the minimum share.',
      recipientOffersDisallowed: 'This owner isn’t accepting offers right now.',
      offerPickControlNotAccepted:
        'This owner’s pick-control setting doesn’t accept this share. Choose a different share.',
      offerOwnershipUnavailable:
        'We couldn’t check this entry’s ownership right now. Please try again.',
      offerBelowSellerMinimum: 'This offer is below the owner’s minimum.',
      fallback: 'This offer couldn’t be submitted.',
    },
    cancelReason: {
      termsChanged: 'Terms changed — review before cancelling.',
      fallback: 'This draft couldn’t be cancelled.',
    },
    offerCap: {
      eligibleCash: 'Eligible cash',
      openOffers: 'Open offers',
      multiplier: 'Multiplier',
      remaining: 'Remaining offer cap',
      remainingAmount: 'Remaining offer cap: {{amount}}',
      unavailable: 'Offer cap unavailable',
    },
  },
  partnerKeys: {
    heading: 'API keys',
    subheading: 'Generate keys for the partner API and revoke them when you’re done.',
    generate: 'Generate key',
    empty: 'You don’t have any API keys yet.',
    loadError: 'We couldn’t load your API keys. Please try again.',
    columns: {
      label: 'Label',
      key: 'Key',
      permissions: 'Permissions',
      rateLimit: 'Rate limit',
      created: 'Created',
      status: 'Status',
      actions: 'Actions',
    },
    permission: {
      scan: 'Scan',
      offer: 'Offer',
      list: 'List',
      none: 'None',
    },
    rateLimitPerMinute: '{{rpm}} / min',
    status: {
      active: 'Active',
      revoked: 'Revoked',
    },
    revoke: 'Revoke',
    generateModal: {
      title: 'Generate API key',
      labelField: 'Label',
      canScan: 'Can scan the marketplace',
      canOffer: 'Can make offers',
      canList: 'Can create, edit, and cancel listings',
      submit: 'Generate key',
      cancel: 'Cancel',
    },
    showOnce: {
      title: 'Key created',
      warning: 'Copy your key now — for security it won’t be shown again.',
      copy: 'Copy key',
      copied: 'Copied',
      done: 'Done',
    },
    revokeModal: {
      title: 'Revoke this key?',
      body: 'Revoke {{label}} ({{prefix}})? Anything using this key will stop working immediately.',
      confirm: 'Revoke key',
      cancel: 'Cancel',
    },
    error: {
      versionConflict: 'This key changed since you loaded it. We’ve refreshed it, so try again.',
      validation: 'Please check the form and try again.',
      generic: 'Something went wrong. Please try again.',
    },
  },
  contest: {
    title: 'Marketplace contest',
    back: 'Back',
    creator: 'Creator:',
    prizes: 'Prizes:',
    stats: {
      aliveEntries: 'Alive entries',
      lowestAsk: 'Lowest ask',
      listings: 'Listings',
    },
    tabs: {
      buyNow: 'Buy now',
      allEntries: 'All entries',
      sold: 'Sold',
    },
    sort: {
      label: 'Sort listings',
      bestValue: 'Best value',
      priceAsc: 'Price low to high',
      priceDesc: 'Price high to low',
    },
    shares: {
      label: 'Filter by share size',
      all: 'All Shares',
      minority: 'Minority (No Pick Control)',
      majority: 'Majority (Pick Control)',
      full: '100% Only (Pick Control)',
    },
    listingsEmpty: 'No open listings in this contest.',
    listingsNoShareMatch: 'No listings match this share size.',
    salesEmpty: 'No sales in this contest yet.',
    salesNoShareMatch: 'No sales match this share size.',
    entriesEmpty: 'No alive entries to show.',
    notTradeableTitle: 'Not on the marketplace',
    notTradeableBody: 'This contest isn’t available to buy or sell entries.',
  },
  card: {
    listings__hasPlural: {
      one: 'Listing',
      other: 'Listings',
    },
    aliveEntries: 'Alive entries',
    startsFrom: 'Starts from',
  },
  grid: {
    columns: {
      entryOwner: 'Entry owner',
      share: 'Share',
      askingPrice: 'Asking price',
    },
    noOpenListings: 'No open listings',
    conveysPickControl: 'Pick control conveys with this listing',
    listingLabel: 'Entry owner {{handle}}, {{share}} share, asking price {{price}}',
    listingLabelWithPickControl:
      'Entry owner {{handle}}, {{share}} share, asking price {{price}}, pick control conveys',
  },
  table: {
    entryOwner: 'Entry owner',
    entry: 'Entry',
    share: 'Share',
    askingPrice: 'Asking price',
    askingPriceNarrow: 'Ask. price',
    notListed: 'Not listed',
    makeOffer: 'Make offer',
    ownershipUnavailable: 'Ownership details are unavailable right now.',
    conveysPickControl: 'Pick control conveys with this listing',
    pickControl: 'Pick control',
    loadMore: 'Load more',
    showingCount: 'Showing {{shown}} of {{total}}',
    sortByAskingPrice: 'Sort by asking price',
    sortByShare: 'Sort by share',
  },
  sales: {
    columns: {
      dateOfSale: 'Date of sale',
      date: 'Date',
      seller: 'Seller',
      buyer: 'Buyer',
      share: 'Share',
      price: 'Price',
      vsEstimate: 'vs est. value',
    },
    stats: {
      aliveEntries: 'Alive entries',
      salesAllTime: 'Sales all time',
      salesAllTimeCaption: 'Full and partials',
      salesLast7Days: 'Sales last 7 days',
      medianPrice: 'Median price',
      perPercent: 'per 1%',
      volumeAllTime: 'Trading volume all time',
      volumeCaption: 'Total value of entries sold',
      vsPreviousWeek: 'vs previous week',
      trendUp: 'Up',
      trendDown: 'Down',
    },
    pickControlTransferred: 'Pick control transferred with this sale',
    perPercent: '{{amount}} per 1%',
    vsEstimate: {
      below: '{{percent}}% below',
      above: '{{percent}}% above',
      at: 'at value',
    },
    vsEstimateArrow: '{{percent}}% est. value',
    vsEstimateLong: {
      below: '{{percent}}% below est. value',
      above: '{{percent}}% above est. value',
      at: 'at est. value',
    },
    dateWithSlate: '{{date}} · {{slate}}',
    transferTo: 'to',
    sortBy: 'Sort by {{column}}',
    panel: {
      title: 'Sold {{date}}',
      close: 'Close',
      salePrice: 'Sale price',
      share: 'Share',
      overview: 'Overview',
      pickHistory: 'Pick history',
      saleInfo: 'Sale info',
      estimatedValue: 'Estimated value at sale',
      estimatedValueTooltip: 'Splash valuation of the share sold, recorded when the sale settled',
      priceVsEstimate: 'Price vs est. value',
      pricePerPercent: 'Price per 1%',
      pickControl: 'Pick control',
      transferred: 'Transferred',
      notTransferred: 'Not transferred',
      seller: 'Seller',
      buyer: 'Buyer',
      teamsUsed: 'Teams used',
      noSnapshot: 'Teams weren’t recorded for this sale',
      noPicks: 'No picks were made before this sale',
      notFoundTitle: 'Sale not found',
      notFoundBody: 'This sale isn’t available.',
      loadErrorTitle: 'We couldn’t load this sale',
      loadErrorBody: 'Please try again.',
    },
  },
  error: {
    title: 'We couldn’t load the data',
    subtitle: 'The marketplace could not be loaded. Please try again.',
    retry: 'Retry',
  },
  listing: {
    title: 'Marketplace listing',
    notFoundTitle: 'No longer available',
    notFoundBody:
      'This listing is no longer available. It may have sold, expired, or been cancelled.',
    ownedTitle: 'You own a share of this entry',
    ownedBody: 'This listing has closed. Manage your share from My holdings.',
    ownedCta: 'View in My holdings',
    backToMarketplace: 'Back to marketplace',
    back: 'Back',
    close: 'Close',
    askingPriceInclFees: 'Asking price incl. fees',
    askingPriceTooltip: 'Includes the platform fee paid by the buyer.',
    share: 'Share',
    tabs: {
      overview: 'Overview',
      pickHistory: 'Pick history',
      otherListings: 'Other listings',
    },
    otherListings: {
      heading: 'Other listings on this entry',
      shareLabel: '{{percent}}% share',
    },
    entryPanel: {
      ownership: 'Ownership',
      ownershipUnavailable: 'Ownership details are unavailable right now.',
      pickController: 'Pick control',
      // A 404 on GET /entries/:id covers both an entry with no marketplace footprint and an
      // unknown/deleted id, so the copy must not assert the entry exists. A transient failure gets
      // errorTitle + createListing.holdingMissing with a retry.
      errorTitle: "Couldn't load this entry",
      noActivityTitle: 'No marketplace activity',
      noActivityBody: 'This entry has no marketplace activity.',
    },
    sellPanel: {
      tabEntry: 'Entry detail',
      tabListing: 'Listing detail',
      tabOffers: 'Offers',
      offersTitle: 'Offers',
      noOffers: 'No offers on your entry yet.',
      listingsTitle: 'Listings',
      listEntry: 'List your entry',
      listRemaining: 'List {{percent}}% remaining',
      expires: 'Expires {{date}}',
    },
    directPanel: {
      estimatedValue: 'Estimated value',
      ownership: 'Ownership',
      ownerHolds: 'Owner holds {{percent}}%',
      ownerHoldsUnknown: 'Ownership unavailable',
      entryOf: "{{handle}}'s Entry",
    },
    offerDetail: {
      tab: 'Offer detail',
      info: 'Offer info',
      price: 'Offer price',
      share: 'Share',
      pickControl: 'Pick control',
      transfersToYou: 'Transfers to you',
      staysWithSeller: 'Stays with seller',
      expires: 'Expires',
      cancel: 'Cancel offer',
      acceptCounter: 'Accept counter',
      rejectCounter: 'Reject counter',
      respondFirst: 'Respond to the counter first',
      statusLabel: 'Status',
    },
    entryInfo: 'Entry info',
    entryLabel: "{{handle}}'s Entry",
    pickControl: 'Pick control',
    pickController: 'Pick controller',
    pickControllerTooltip:
      "The user selling this entry doesn't make the picks. The pick controller is listed here.",
    pickControlTooltip:
      'Pick control means you make this entry’s picks after you buy. Without it, the current controller keeps making them.',
    yes: 'Yes',
    no: 'No',
    splashValuation: 'Estimated value',
    valuationTooltip: 'Total contest prize divided by alive entries, scaled to listed share',
    valuationBelow: '{{amount}} ({{percent}}% below)',
    valuationAbove: '{{amount}} ({{percent}}% above)',
    expireIn: 'Expires',
    expiresInDays__hasPlural: {
      one: '{{count}} day',
      other: '{{count}} days',
    },
    expiresInHours__hasPlural: {
      one: '{{count}} hour',
      other: '{{count}} hours',
    },
    expiresInMinutes__hasPlural: {
      one: '{{count}} minute',
      other: '{{count}} minutes',
    },
    expired: 'Expired',
    picks: 'Picks',
    pickHistory: 'Pick history',
    makeOffer: 'Make offer',
    buyNow: 'Buy now',
    manageOwnListing: 'Manage in My listings',
    eliminated: 'Eliminated',
    entryEliminated: 'This entry has been eliminated.',
    chips: {
      pickControl: 'Pick control',
      aliveEntries: 'Alive entries',
      lives: 'Lives',
      slate: 'Slate',
    },
    orderSummary: 'Order summary',
    askingPrice: 'Asking Price',
    platformFee: 'Platform fee ({{percent}}%)',
    total: 'Total',
    confirmPurchase: 'Confirm purchase',
    reviewYourPurchase: 'Review your purchase',
    chargeSummary:
      '{{total}} will be charged to your Splash wallet. The {{asking}} asking price plus a {{fee}} buyer fee. This purchase is final.',
    ownBulletLead: "You'll own {{percent}}% right away.",
    ownBulletBody: 'The share transfers to you as soon as you confirm.',
    controlBulletLeadYes: 'You make the picks.',
    controlBulletBodyYes:
      "As the majority owner you are now responsible for making picks for this entry. When pick control transfers, the entry's current and upcoming picks are cleared, so you'll need to make your pick before the pick deadline.",
    controlBulletLeadNo: "You won't control the picks.",
    controlBulletBodyNo:
      'This is a minority share, so the majority owner makes the picks for this entry.',
    ownershipBlock:
      "Ownership details for this entry can't be determined right now. Please try again later.",
    ownershipContextUnavailableBlock:
      'Ownership data is temporarily unavailable. Please try again later.',
    balanceLoading: 'Checking your balance…',
    balanceUnavailable: 'Your balance could not be loaded. Please try again.',
    notEnoughCash: 'Not enough cash ({{amount}}). You need to deposit money first.',
    depositToPay: 'Deposit to pay',
    purchaseComplete: 'Purchase complete',
    purchaseSummary: 'You bought {{percent}}% of {{contest}} Entry #{{order}}',
    makePicks: 'Make picks',
    viewHolding: 'View holding',
    errorTitle: 'Something went wrong',
    errorBody: 'The purchase could not be completed. Please try again.',
    // Buy-now raced a seller edit (LISTING_TERMS_CHANGED); the buyer reviews the fresh terms.
    termsChanged:
      'The seller updated this listing. Review the new price and share, then buy again.',
    contactSupport: 'Contact support',
    tryAgain: 'Try again',
    locationTitle: 'Location not permitted',
    locationBody: 'Buying entries is not available in your current location.',
    verifyTitle: 'Verify your identity',
    verifyBody: 'You need to verify your identity before buying entries.',
    verifyNow: 'Verify now',
    notNow: 'Not now',
    kycError: 'Identity status could not be loaded.',
    kycRetry: 'Try again',
    loadError: 'This listing could not be loaded.',
    loadRetry: 'Try again',
  },
  offer: {
    title: 'Make offer',
    yourOffer: 'Your offer',
    sharePercent: 'Share %',
    custom: 'Custom',
    amount: 'Amount',
    amountPlaceholder: '$0',
    balance: 'Balance: {{amount}}',
    buyerFee: 'Buyer fee ({{percent}}%)',
    suggested: 'Suggested: {{amount}} (via Estimated value). Min {{min}}.',
    suggestedUnavailable: 'Min {{min}}.',
    suggestedEstimate: 'Suggested: {{amount}} (estimated value).',
    maxPrice: 'Max {{max}}.',
    minimumOffer: 'Minimum offer: {{amount}}',
    startsAt: 'Offers on this entry start at {{amount}}.',
    useMinimum: 'Offer {{amount}}',
    raiseToContinue: 'Raise your offer to at least {{amount}} to continue.',
    ownerRequiresPickControl:
      'Owner only takes offers that leave you with 51% or more (pick control moves to you).',
    ownerDisallowsPickControl:
      'Owner only takes offers that leave you under 51% (they keep pick control).',
    offersOffTitle: 'Offers are off',
    offersOffBody: "The owner isn't taking offers on this entry.",
    offersOffBodyUnlisted: "The owner isn't taking offers on this entry. Check back later.",
    buyNowFor: 'Buy now for {{amount}}',
    backToEntry: 'Back to entry',
    placeOffer: 'Place offer',
    chips: {
      askingPriceInclFees: 'Asking price incl. fees',
      ownership: 'Ownership',
    },
    controlTransfers: 'Pick control will transfer to you',
    controlStays: "Pick control will stay with the entry's owner",
    pickControlUnknown:
      "Pick control for this entry can't be determined right now. Please try again later.",
    shareInvalid: 'Enter a share between {{min}}% and {{max}}%.',
    shareBands:
      'Offer 1–{{keep}}% (the seller keeps majority) or {{buyer}}–{{listed}}% (you take majority).',
    shareBandsBuyerOnly: 'Offer {{buyer}}–{{listed}}% so you take majority.',
    shareBandsKeepOnly: 'Offer 1–{{keep}}% so the seller keeps majority.',
    shareListedCap: 'Offer 1–{{listed}}%, the share listed.',
    shareNoBand:
      "No share of this stake would leave anyone with a majority, so it can't take offers.",
    noMajorityBand:
      "You can't make an offer, no transaction would leave a majority pick controller.",
    placedTitle: 'Offer placed',
    placedBody:
      'Your {{amount}} offer for {{percent}}% Entry in {{contest}} is live. Your funds are locked in escrow until the seller responds, you cancel, or the listing expires.',
    placedBodyOfferCap:
      'Your {{amount}} offer for {{percent}}% Entry in {{contest}} is live. It stands against your offer cap — no cash is held — until the seller responds, you cancel, or the listing expires.',
    ownership: 'Ownership',
    offerPrice: 'Offer price',
    totalEscrowed: 'Total escrowed',
    totalAgainstOfferCap: 'Against your offer cap',
    viewMyOffers: 'View my offers',
    errorBody: 'The offer could not be placed. Please try again.',
    offerCapError: {
      exceedsCash: 'This offer is more than your eligible cash. Lower it to place the offer.',
      accountCeiling:
        'This offer is above the limit set on your account. Lower it to place the offer.',
      offerCapLimit:
        'This would put your open offers over your offer cap. Cancel an open offer or lower this one.',
      balanceUnavailable: 'We couldn’t check your balance just now. Please try again shortly.',
      cashCollectNotEnabled:
        "This offer can't be accepted yet. Cash collection for offer-cap offers isn't enabled.",
    },
    // "Review your offer" confirm modal, shared by the listing and direct offer forms.
    // The pick bullet swaps its lead/body on conveyance; when ownership can't be read it is replaced
    // by the offer.pickControlUnknown notice and the primary disables.
    review: {
      title: 'Review your offer',
      lead: 'Please confirm your {{amount}} offer for {{percent}}%',
      escrowLead: 'Your funds will be held in escrow.',
      escrowBody:
        '{{amount}} will be taken from your wallet now and held until the seller accepts, declines, the offer expires, or you cancel the offer.',
      offerCapLead: 'This offer draws against your offer cap.',
      offerCapBody:
        'No cash is taken now — {{amount}} stands against your offer cap until the seller accepts, declines, the offer expires, or you cancel it. Up to {{perOffer}} per offer; remaining offer cap {{remaining}}.',
      offerCapBodyNoRemaining:
        'No cash is taken now — {{amount}} stands against your offer cap until the seller accepts, declines, the offer expires, or you cancel it.',
      controlLeadYes: "You'll get pick control.",
      controlBodyYes:
        "This offer is for a majority share, so if it's accepted pick control transfers to you, the upcoming picks are cleared, and you'll need to make picks for this entry going forward.",
      controlLeadNo: "You won't control the picks.",
      controlBodyNo:
        "This offer is for a minority share, so if it's accepted you won't have pick control. The majority owner makes the picks for this entry.",
      expiresLead: 'Expires 90 minutes before the pick deadline.',
      expiresBody:
        'Your offer stays live until 90 minutes before the pick deadline. You can cancel it anytime before then.',
      cancel: 'Cancel',
    },
  },
  directOffer: {
    title: 'Make offer',
    to: 'To {{handle}}',
    ownership: 'Their ownership',
    placed: 'Offer sent',
    placedToast: 'Your funds are in escrow. Track it under Offers.',
    errorBody: 'The offer could not be placed. Please try again.',
    // 422 from the server: the requested share would leave the entry with no majority owner.
    noMajorityHint:
      "An offer this size would leave the entry with no majority owner. Lower your share or offer for the owner's full stake.",
    locationTitle: 'Location not permitted',
    locationBody: 'Making offers is not available in your current location.',
  },
  holdings: {
    title: 'My Holdings',
    tabs: {
      holdings: 'My holdings',
      listings: 'My listings',
      offers: 'Offers',
    },
    entriesCount__hasPlural: {
      one: '{{count}} entry',
      other: '{{count}} entries',
    },
    offersBadge__hasPlural: {
      one: '{{count}} offer',
      other: '{{count}} offers',
    },
    filterByContest: 'Filter by contest',
    filterClose: 'Close filter',
    filterSearchPlaceholder: 'Search contest',
    filterNoMatches: 'No contests match',
    filterLoading: 'Loading contests…',
    filterLoadError: "Couldn't load your contests. Close and reopen to try again.",
    filterPartial: 'Some of your contests may be missing from this list.',
    filterAtCap: 'You can filter on up to {{count}} contests at once',
    filterClear: 'Clear',
    filterApply: 'Apply',
    columns: {
      entry: 'Entry',
      ownership: 'Ownership',
      ownershipMobile: 'Share',
      pickControl: 'Pick control',
      estimatedValue: 'Estimated value',
      estimatedValueMobile: 'EST. VALUE',
      cost: 'Cost',
    },
    sortByOwnership: 'Sort by ownership',
    sortByEstimatedValue: 'Sort by estimated value',
    entryOrder: 'Entry {{order}}',
    entryUnknown: 'Entry',
    eliminated: 'Eliminated',
    yes: 'Yes',
    no: 'No',
    empty: 'You have no marketplace holdings yet.',
    filteredEmpty: 'No holdings match this filter.',
    detail: {
      projectedPayout: 'Projected payout',
      projectedPayoutTooltip:
        'Estimated value: total contest prize divided by alive entries, scaled to your share.',
      ownership: 'Ownership',
      entryInfo: 'Entry info',
      pickControl: 'Pick control',
      costBasis: 'Cost basis',
      offersReceived: 'Offers received',
      close: 'Close',
      activeListings: 'Active listings',
      listingsCount__hasPlural: {
        one: '{{count}} listing',
        other: '{{count}} listings',
      },
      percentReserved: '{{percent}}% reserved',
      cancelListing: 'Cancel listing',
      capTable: 'Cap table',
      capTableYou: 'You',
      contestInfo: 'Contest info',
      type: 'Type',
      typeSurvivor: 'Survivor',
      prizes: 'Prizes',
      aliveEntries: 'Alive entries',
      slate: 'Slate',
      contestPage: 'Contest page',
      viewContest: 'View contest',
      listForSale: 'List my entry for sale',
      eliminatedCannotList: "Eliminated entries can't be listed.",
      pausedCannotList: 'Marketplace paused for ongoing games',
      noSlateCannotList: 'Listing opens when the next slate is scheduled.',
    },
  },
  createListing: {
    title: 'List my entry',
    back: 'Back',
    close: 'Close',
    // Block-screen copy, keyed by the closed reason that genuinely has nothing to list against.
    blocked: {
      noSlateTitle: 'No upcoming slate',
      noSlateBody: 'This entry can’t be listed until the next slate is scheduled.',
      notEnabledTitle: 'Marketplace disabled',
      notEnabledBody: 'The marketplace is disabled for this contest.',
      contestClosedTitle: 'Marketplace closed',
      contestClosedBody: "This contest's marketplace is closed.",
      genericTitle: 'Marketplace paused',
      genericBody: 'This entry can’t be listed right now.',
    },
    yourOwnership: 'Your ownership',
    listings: 'Listings',
    removeListing: 'Remove listing {{ordinal}}',
    shareLabel: 'Share %',
    priceLabel: 'Asking price',
    keepControl: 'You keep the pick control if this listing sells.',
    transferControl: "Pick control transfers to this listing's buyer.",
    estimate: 'Estimated value of {{percent}}% ≈ {{amount}}',
    estimatePlaceholder: 'Enter share for estimated value',
    expiresDefault: 'Expires: 90m from pick lock',
    expiresDefaultWithDate: 'Expires: 90m from pick lock ({{date}})',
    expiresCustom: 'Expires: {{date}}',
    expiresEdit: 'Edit',
    customExpiryTitle: 'Set custom expiration date and time',
    customExpiryCancel: 'Cancel',
    dateLabel: 'Date',
    timeLabel: 'Time ({{timezone}})',
    expiryPast: 'Enter a future expiration date and time.',
    expiryOutOfRange: 'Expiration must be at least {{minutes}} minutes before the pick deadline.',
    addAnother: 'Add another listing of this entry',
    totalShare: 'Total share to list',
    totalShareValue: '{{percent}}% of {{available}}%',
    totalAsking: 'Total asking',
    fullSaleReceive: 'You will receive on a full sale',
    fullSaleTooltip: 'The total asking price minus the seller fee.',
    review: 'Review listing',
    exceedsAvailable: 'All listings together exceed {{available}}% available',
    noMajority:
      'Listing {{percent}}% leaves no majority owner. Enter 1–{{keep}}% (you keep majority) or {{buyer}}–{{owned}}% (the buyer takes majority).',
    noMajorityBuyerOnly:
      'Listing {{percent}}% leaves no majority owner. Enter {{buyer}}–{{owned}}% so the buyer takes majority.',
    exact51WholeOnly: 'A 51% share can only be sold whole.',
    noMajorityMinority:
      'As the pick controller you can only sell in a way that leaves someone with a majority.',
    shareInvalid: 'Enter a share between 1% and {{max}}%.',
    priceInvalid: 'Enter an asking price above $0.',
    priceAboveEstimatedMax: 'Maximum asking price is {{maxMultiplier}}x est. value, {{amount}}.',
    summaryTitle: 'Listing summary',
    summaryYourListing: 'Your listing',
    summaryAsking: 'Asking {{amount}}',
    summaryFees: 'Fee {{fee}} • You receive {{net}}',
    summaryAutoExpires: 'Auto-expires 90 minutes before the pick deadline',
    summaryExpires: 'Expires {{date}}',
    shareYouKeep: 'Share you keep',
    priceFarFromEstimate: {
      belowLabel:
        'I understand this price is {{percent}}% below estimated value and wish to continue.',
      aboveLabel:
        'I understand this price is {{percent}}% above estimated value and wish to continue.',
    },
    submit: 'Submit your listing',
    cancelAnyTime: 'You can cancel any listing before it sells.',
    submitError: 'The listing could not be created. Please try again.',
    partialNote: 'Listings before the failed one were created and are live in My listings.',
    successTitle__hasPlural: {
      one: 'Your entry is listed',
      other: 'Your {{count}} listings are live',
    },
    successBody__hasPlural: {
      one: "{{percent}}% of your {{contest}}'s entry is now live in the marketplace. Buyers can purchase at asking price or send you an offer.",
      other:
        "{{count}} listings totaling {{percent}}% of your {{contest}}'s entry are now live in the marketplace. Buyers can purchase at asking price or send you an offer.",
    },
    successBodyNoContest__hasPlural: {
      one: '{{percent}}% of your entry is now live in the marketplace. Buyers can purchase at asking price or send you an offer.',
      other:
        '{{count}} listings totaling {{percent}}% of your entry are now live in the marketplace. Buyers can purchase at asking price or send you an offer.',
    },
    successShareListed: 'Share % listed',
    successAskingPrice: 'Asking price',
    successActiveListings: 'Active listings on this entry',
    successNotify: 'You will be notified the moment of selling or receiving an offer.',
    viewMyListings: 'View in my listings',
    holdingMissing: 'This entry could not be loaded. Please try again.',
  },
  myListings: {
    activeCount__hasPlural: {
      one: '{{count}} active listing',
      other: '{{count}} active listings',
    },
    columns: {
      entry: 'Entry',
      listedShare: 'Listed share',
      listedShareMobile: 'SHARE',
      pickControl: 'Pick control',
      pickControlMobile: 'PICK',
      askingPrice: 'Asking price',
      askingPriceNarrow: 'PRICE',
      status: 'Status',
    },
    stays: 'Stays',
    transfers: 'Transfers',
    status: {
      active: 'Active',
      pending_settlement: 'Pending settlement',
      filled: 'Filled',
      cancelled: 'Cancelled',
      expired: 'Expired',
      expiringSoon: 'Expiring soon',
    },
    offersCount__hasPlural: {
      one: '{{count}} offer',
      other: '{{count}} offers',
    },
    listingsCount__hasPlural: {
      one: '{{count}} listing',
      other: '{{count}} listings',
    },
    viewLabel: 'Listing status',
    views: {
      active: 'Active',
      closed: 'Closed',
    },
    columnsClosed: {
      share: 'Share',
      price: 'Price',
    },
    listingOrdinal: 'Listing {{ordinal}}',
    afterFees: '{{amount}} after fees',
    soldStatus: 'Sold',
    expandEntry: 'Show listings for {{entry}}',
    collapseEntry: 'Hide listings for {{entry}}',
    empty: 'You have no listings yet.',
    emptyActive: 'You have no active listings yet.',
    emptyClosed: 'You have no closed listings yet.',
  },
  editListing: {
    title: 'Edit my listing',
    back: 'Back',
    close: 'Close',
    yourOwnership: 'Your ownership',
    listings: 'Listings',
    shareLabel: 'Share %',
    priceLabel: 'Asking price',
    keepControl: 'You keep the pick control if this listing sells.',
    transferControl: 'Pick control transfers to the buyer if this listing sells.',
    estimate: 'Estimated value of {{percent}}% ≈ {{amount}}',
    expiresRow: 'Expires: {{date}}',
    expiresEdit: 'Edit',
    expiresLabel: 'Expires ({{timezone}})',
    totalShare: 'Total share to list',
    totalShareValue: '{{percent}}% of {{total}}',
    totalAsking: 'Total asking',
    fullSaleReceive: 'You will receive on a full sale',
    fullSaleTooltip: 'The total asking price minus the seller fee.',
    update: 'Update listing',
    expiresInvalid: 'Enter a future expiration date.',
    noChanges: 'Nothing to update yet.',
    gone: 'This listing is no longer active.',
    errorBody: 'The listing could not be updated. Please try again.',
    confirm: {
      title: 'Confirm listing update',
      body: 'Review the changes you made before you update your listing.',
      offersWarning__hasPlural: {
        one: '{{count}} pending offer may be declined.',
        other: '{{count}} pending offers may be declined.',
      },
      offersWarningBody:
        'Offers that expire after your new date are declined automatically when you shorten the expiration.',
      shareListed: 'Share listed',
      askingPrice: 'Asking price',
      youReceive: 'You receive if sold',
      expires: 'Expires',
      // Editing the share can change whether a sale conveys pick control.
      transfersLead: 'Pick control transfers to the buyer.',
      transfersBody:
        "If this listing sells, the buyer becomes the majority owner and makes the picks for this entry. The entry's current and upcoming picks are cleared when control transfers.",
      staysLead: 'You keep pick control.',
      staysBody:
        'This listing is for a minority share, so you keep making the picks for this entry after it sells.',
      unchangedLead: "Pick control doesn't change.",
      unchangedBody:
        "You don't control the picks for this entry, so this sale won't change who makes them.",
      confirm: 'Confirm & Update',
    },
  },
  cancelListing: {
    title: 'Cancel this listing?',
    body: 'Your {{percent}}% listing at {{amount}} on {{entry}} will be removed from the marketplace. The share stays yours and you can list this entry again any time.',
    offersWarning__hasPlural: {
      one: '{{count}} pending offer will be declined.',
      other: '{{count}} pending offers will be declined.',
    },
    offersWarningBody:
      'Offers were made on your current terms, so cancelling the listing closes them automatically.',
    confirm: 'Cancel listing',
    confirmWithOffers: 'Cancel and decline offers',
    keep: 'Keep listing',
    error: 'The listing could not be cancelled. Please try again.',
  },
  sellerListing: {
    tabs: {
      overview: 'Overview',
      offers: 'Offers',
    },
    close: 'Close',
    entrySubtitle: '{{entry}} • {{contest}}',
    listingInfo: 'Listing info',
    askingPrice: 'Asking price',
    youReceive: 'You receive if sold after fees',
    youReceiveTooltip: 'The asking price minus the seller fee.',
    share: 'Share',
    pickControl: 'Pick control',
    staysWithYou: 'Stays with you',
    transfersToBuyer: 'Transfers to buyer',
    expires: 'Expires',
    entryDetail: 'Entry detail',
    viewEntry: 'View entry',
    editListing: 'Edit listing',
    cancelListing: 'Cancel listing',
    offersTitle: 'Offers',
    offerShare: '{{percent}}% share',
    offerExpires: 'Expires {{date}}',
    offerDeclinedSummary: '{{amount}} for {{percent}}%',
    accept: 'Accept',
    counter: 'Counter',
    decline: 'Decline',
    declined: 'Declined',
    noOffersTitle: 'No offers yet',
    noOffersBody:
      "Your listing is live and visible to buyers. You'll get a notification the moment an offer comes in.",
    // Terminal-listing offers empty states: only an ACTIVE listing says
    // "No offers yet"; a closed one states its terminal state instead.
    offersCancelledTitle: 'This listing was cancelled.',
    offersCancelledHoldings: 'This listing closed because your holdings changed.',
    offersExpiredTitle: 'This listing expired.',
    offersSoldTitle: 'This listing sold.',
    offersInactiveTitle: 'This listing is no longer active.',
    sold: {
      title: 'Listing sold',
      summary: 'You sold {{percent}}% of {{entry}} to {{buyer}} for {{amount}}.',
      summaryFullSale: 'This entry is no longer yours.',
      youStillOwn: 'You still own',
      pickControl: 'Pick control',
      pickControlYou: 'You',
      pickControlTransferred: 'Transferred',
      otherListings: 'Other listings',
      soldTo: 'Sold to',
      when: 'When',
      saleType: 'Sale type',
      saleTypeAcceptedOffer: 'Accepted offer',
      saleTypeBuyNow: 'Bought for asked price',
      salePrice: 'Sale price',
      sellerFee: 'Seller fee ({{percent}}%)',
      addedToBalance: 'Added to balance',
      relistRemainder: 'Relist remainder',
      goToEntry: 'Go to my entry',
      backToActive: 'Back to active listings',
    },
  },
  offers: {
    // Offers page (Buy / Sell) — the categorised, paginated list. Buy = offers I
    // made; Sell = offers on my shares. The list is one stream ordered pending → countered → closed
    // with a section break on each category change; there is no Active/Closed toggle.
    buying: 'Buying',
    selling: 'Selling',
    scopeLabel: 'Offer status',
    sortByPickControl: 'Sort by pick control',
    scopeActive: 'Active',
    scopeAll: 'All',
    scopeCounters: 'Counters',
    buyingCount__hasPlural: {
      one: '{{count}} buying offer',
      other: '{{count}} buying offers',
    },
    sellingCount__hasPlural: {
      one: '{{count}} selling offer',
      other: '{{count}} selling offers',
    },
    sections: {
      pending: 'Pending',
      countered: 'Countered',
      closed: 'Closed',
    },
    empty: {
      buyTitle: "You haven't made any offers",
      buyBody:
        "See an entry you want? Make an offer even if it isn't listed. The owner can accept, counter, or decline, and nothing is charged unless they accept.",
      buyCta: 'Browse entries',
      countersTitle: 'No counters waiting on you',
      countersBody:
        'When a seller counters one of your offers, it shows up here for you to accept or decline.',
      sellTitle: 'No offers on your entries yet',
      sellBody:
        "You'll get a notification the moment an offer comes in. No need to keep checking here.",
    },
    entryOf: "{{handle}}'s Entry",
    pickTransfers: 'Transfers',
    pickStays: 'Stays',
    cols: {
      entry: 'Entry',
      from: 'From',
      pickControl: 'Pick control',
      offer: 'Offer',
      expires: 'Expires',
    },
    // The mobile card's detail panel: three labelled cells shared by Buy and
    // Sell. Pick control reads Yes/No — Yes when the offer conveys pick control to the buyer.
    panel: {
      offeredPrice: 'Offered price',
      share: 'Share',
      sharePercent: '{{percent}}%',
      pickYes: 'Yes',
      pickNo: 'No',
    },
    entry: 'Entry {{order}}',
    entryUnknown: 'An entry',
    shareLabel: '{{percent}}% share',
    percentOfEv: '{{percent}}% of EV',
    evUnknown: 'EV unknown',
    alertSuppressed: {
      belowAlertMinimum: 'Under your {{percent}}% of EV alert minimum. No alert sent.',
      alertsOff: 'Your offer alerts are off. No alert sent.',
    },
    timeLeft: {
      days: '{{count}}d left',
      hours: '{{count}}h left',
      minutes: '{{count}}m left',
      expired: 'Expired',
    },
    escrowed: 'Escrowed',
    counterAt: 'Countered at {{amount}}',
    from: 'From {{handle}}',
    to: 'To {{handle}}',
    status: {
      pending: 'Pending',
      accepted: 'Accepted',
      rejected: 'Declined',
      expired: 'Expired',
      cancelled: 'Cancelled',
      failed: 'Failed',
      listingFilled: 'Closed — the listing sold',
      countered: 'Countered',
    },
    actions: {
      accept: 'Accept',
      reject: 'Decline',
      counter: 'Counter',
      cancel: 'Cancel offer',
      withdrawCounter: 'Withdraw counter',
    },
    emptyMine: 'You have not made any offers yet.',
    emptyReceived: 'No offers on your listings right now.',
    accept: {
      title: 'Accept offer?',
      selling: 'You are selling {{percent}} of {{entry}} for {{amount}}.',
      buying: 'You are buying {{percent}} of {{entry}} for {{amount}}.',
      sellerReceives: 'You receive {{amount}}, paid to your wallet.',
      buyerPays: 'You pay {{amount}} from your wallet.',
      keepsRemainder:
        'You keep {{remaining}}% of {{entry}}. You can relist the remainder afterwards.',
      sellsWholeStake: 'You sell your whole stake.',
      offerRow: 'Offer',
      youReceive: 'You receive',
      sellerFee: 'Seller fee ({{percent}}%)',
      offerPrice: 'Offer price',
      shareSold: 'Share sold',
      confirm: 'Accept this offer',
      acceptedTitle: 'Offer accepted',
      acceptedBody: 'The sale settled and your listing is now closed.',
      acceptedBodyBuyer: 'The sale settled. Your share is in your holdings.',
      soldTitle: 'Sold to {{buyer}}',
      soldShare: '{{percent}} of {{entry}}',
      controlStays: 'Pick control stays with you.',
      controlTransferred: 'Pick control transferred to the buyer.',
      acceptedOffer: 'Accepted offer',
      addedToBalance: 'Added to your balance',
      relistRemainder: 'Relist remainder',
      done: 'Done',
      goneBody: 'This offer is no longer available. It may have expired or been withdrawn.',
      errorBody: 'The offer could not be accepted. Please try again.',
      // Seller-voiced CONCENTRATION_CAP_EXCEEDED: the buyer is the one at the contest cap.
      buyerAtCap:
        "The buyer has reached the marketplace limit of {{n}} active entries in this contest, so this offer can't be accepted.",
      buyerAtCapUnknown:
        "The buyer has reached the marketplace limit of active entries in this contest, so this offer can't be accepted.",
    },
    counterModal: {
      title: 'Counter offer',
      body: 'Propose a new price for {{percent}}% of this entry. The buyer can accept, reject, or cancel.',
      shareLabel: 'New asking share',
      amount: 'New asking price',
      submit: 'Send counter offer',
      invalid: 'Enter a counter price above $0.',
      invalidShare: 'Enter a counter share between 0% and 100%.',
      noMajority:
        "You can't send this counter, no transaction would leave a majority pick controller.",
      expiryBody:
        'Your counter will expire at {{expires}}. You can withdraw it until they respond.',
      expiredBody: 'This offer is expired, you can no longer counter.',
      transfersLead: 'Pick control transfers to the buyer.',
      transfersBody:
        "If this counteroffer is accepted the buyer becomes the majority owner and makes the picks for this entry. The entry's current and upcoming picks are cleared when control transfers.",
      staysLead: 'You keep pick control.',
      staysBody:
        'This share is a minority, so you keep making the picks for this entry after the sale.',
      unchangedLead: "Pick control doesn't change.",
      unchangedBody:
        "You don't control the picks for this entry, so this sale won't change who makes them.",
      errorBody: 'The counter could not be sent. Please try again.',
    },
    counterCard: {
      yourCounterOffer: 'Your counter offer',
      cancel: 'Cancel',
      youCountered: 'You countered',
      offeredLine: '{{buyer}} offered {{amount}} for {{percent}}.',
      counteredLine: 'Countered at {{amount}} for {{percent}}',
      // One counter per offer; there is no edit endpoint.
      withdrawCounter: 'Withdraw counter',
    },
    decline: {
      title: 'Decline this offer?',
      body: "Declining releases {{buyer}}'s {{amount}} from escrow back to them. This cannot be undone.",
      bodyOfferCap:
        "Declining releases {{buyer}}'s {{amount}} offer from their offer cap. No money moves. This cannot be undone.",
      confirm: 'Decline offer',
      withdrawTitle: 'Withdraw your counter?',
      withdrawBody:
        "The offer returns to {{buyer}}'s original {{amount}} for {{share}}. You can accept, decline, or counter again.",
      withdrawConfirm: 'Withdraw counter',
      cancelTitle: 'Cancel this offer?',
      cancelBody: 'This withdraws your {{amount}} offer and releases the funds you have in escrow.',
      cancelBodyOfferCap:
        'This withdraws your {{amount}} offer and releases it from your offer cap. No money moves.',
      cancelConfirm: 'Cancel offer',
      rejectCounterTitle: 'Decline this counter?',
      rejectCounterBody:
        "Declining {{seller}}'s counter returns your {{amount}} from escrow to your wallet. This cannot be undone.",
      rejectCounterBodyOfferCap:
        "Declining {{seller}}'s counter releases your {{amount}} offer from your offer cap. No money moves. This cannot be undone.",
      rejectCounterConfirm: 'Decline counter',
      keep: 'Keep offer',
      keepCounter: 'Keep counter',
    },
    // Toasts: offer-action results surface as notifications, never as a
    // banner above the columns.
    toast: {
      declined: 'Offer declined.',
      counterDeclined: 'Counter declined.',
      counterWithdrawn: 'Counter withdrawn.',
      // The seller's withdraw raced a resolution (buyer accepted, or it already reverted).
      counterAlreadyResolved: 'This counter was already resolved.',
      // The buyer's accept raced the seller's withdraw — the counter reverted, so the
      // buyer's own original offer is what stands now.
      counterWithdrawnBuyer: 'This counteroffer was withdrawn. Your original offer is still open.',
      // Any other unauthorized accept (not the reverted-counter race).
      cannotAccept: "You can't accept this offer.",
      cancelled: 'Offer cancelled.',
      cancelFundingBlocked:
        'This offer can’t be cancelled because its payment has already started.',
      declineFundingBlocked:
        'This offer can’t be declined because its payment has already started.',
      rejectCounterFundingBlocked:
        'This counter can’t be declined because the offer’s payment has already started.',
      error: 'That could not be completed. Please try again.',
      walletUnavailable: 'Wallet unavailable — try again shortly.',
    },
    actionError: 'That could not be completed. Please try again.',
  },
  offerSettings: {
    title: 'Notification Settings',
    saveFailed: "Couldn't save. Try again.",
    revisionConflict: 'These offer settings changed since you loaded them. Reload and try again.',
    reloadLatest: 'Reload',
    toast: {
      saved: 'Offer settings saved',
      reset: 'Offer settings reset to defaults',
      contestRemoved: '{{name}} now uses your all-entries settings',
      undo: 'Undo',
      undoFailed: "Couldn't undo. Set the contest up again from Add a contest.",
    },
    unsaved: {
      title: 'Leave without saving?',
      body: "You have changes you haven't saved. If you leave now, they'll be lost.",
      leave: 'Discard changes',
      keep: 'Keep editing',
      saveAndLeave: 'Save and leave',
    },
    unsavedBar: 'Unsaved changes',
    saveAll: 'Save settings',
    saveAllEntries: 'Save all entries settings',
    saveContest: 'Save contest',
    removeConfirm: {
      title: 'Remove {{name}} settings?',
      body: 'This contest will go back to your all-entries settings.',
      confirm: 'Remove',
    },
    resetDefaults: 'Reset to defaults',
    resetConfirm: {
      labels: {
        receiveOffers: 'Receive offers',
        pickControl: 'Pick control',
        minimum: 'Minimum offer',
        alerts: 'Offer alerts',
        alertAbove: 'Alert me for offers above',
      },
      title: 'Reset to defaults?',
      body: 'Does not remove per-contest settings.',
      unsavedLost: 'Your unsaved changes will be lost.',
      confirm: 'Reset now',
      on: 'On',
      off: 'Off',
      percentOfEv: '{{percent}}% of EV',
      example: '{{amount}} on an entry with an EV of $100',
      exampleLabel: 'Example on a $100 entry',
    },
    save: 'Save',
    cancel: 'Cancel',
    remove: 'Remove',
    edit: 'Edit',
    editContest: 'Edit {{name}}',
    removeContest: 'Remove {{name}}',
    back: 'Back to Settings',
    backToAdd: 'Back to Add contest',
    loadError: "We couldn't load your settings. Try again.",
    retry: 'Try again',
    contestUnavailable: "That contest's settings aren't available right now.",
    offersOff: 'Offers are off.',
    evUnknown: "Minimum and alert floors only apply when the entry's estimated value is known.",
    allEntries: {
      heading: 'All entries',
      evInfo:
        'EV (estimated value) is total contest prize divided by alive entries, scaled by share percentage.',
      evInfoLabel: 'What is EV?',
      offersSection: 'All entries · Offers',
      alertsSection: 'All entries · Alerts',
    },
    receiveOffers: {
      label: 'Receive offers',
      info: 'Allow users to make new offers on your unlisted entries.',
      infoLabel: 'About receiving offers',
    },
    pickControl: {
      label: 'Allowed offers',
      any: 'Any offer',
      with: 'Only offers that transfer pick control',
      without: "Only offers that don't transfer pick control",
      anyShort: 'Any',
      withShort: 'Transfers',
      withoutShort: 'No transfer',
      info: 'Pick control means the buyer makes the picks after the sale. It only moves when the buyer ends up owning 51% or more of an entry.',
      infoLabel: 'What is pick control?',
    },
    minimum: {
      label: 'Show buyers your minimum offer (% of EV)',
      inputLabel: 'Minimum offer, % of EV',
      example: '<0>{{amount}}</0> on an entry with an EV of $100.',
      exampleEntry: '<0>{{amount}}</0> for your full entry.',
      none: 'No minimum. Any offer can come in.',
      defaultNone: 'Default is no minimum.',
      defaultIs: 'Default is {{percent}}%.',
    },
    alerts: {
      label: 'Receive email and in-app alerts for new offers',
      info: "Turn off to stop alerts for new offers. You'll still hear about sales and accepted offers. All offers will still be available in the Offers tab.",
      infoLabel: 'About offer alerts',
      aboveLabel: 'Alert me for offers above threshold',
      aboveHelper: 'Lower offers still show up in Offers, just without an alert.',
      inputLabel: 'Alert above, % of EV',
      example: 'No alert under <0>{{amount}}</0> on an entry with an EV of $100.',
      exampleEntry: 'No alert under <0>{{amount}}</0> for your full entry.',
      defaultIs: 'Default is {{percent}}%.',
      none: 'No alerts for new offers.',
      turnOnToUse: 'Turn on alerts to use this.',
      belowMinimum: "Alerts can't start below your minimum offer ({{percent}}%).",
      matchMinimum: 'Set alerts to {{percent}}%',
    },
    percentSuffix: '%',
    percentInvalid: 'Enter a % from 0 to 10,000.',
    notifyBelowReject:
      "Alerts can't start below your minimum offer. Raise the alert % or lower your minimum offer.",
    outcome: {
      caption: 'What happens to offers',
      blocked: 'Below minimum',
      noAlert: 'No alert',
      offersOff: 'Offers off',
      alert: 'Alert',
      under: 'under {{percent}}%',
      range: '{{from}}–{{to}}%',
      from: '{{percent}}%+',
      everyOffer: 'every offer',
    },
    contests: {
      heading: 'Contest settings',
      headingCount: 'Contest settings ({{count}})',
      overrideCount__hasPlural: {
        one: '{{count}} contest overrides your settings above',
        other: '{{count}} contests override your settings above',
      },
      add: '+ Add contest',
      cols: {
        contest: 'Contest',
        receiveOffers: 'Receive offers',
        pickControl: 'Pick control',
        minimum: 'Minimum',
        alerts: 'Alerts',
        alertAbove: 'Alert above',
        actions: 'Actions',
      },
      same: 'Default',
      sameWith: 'Default ({{value}})',
      on: 'On',
      off: 'Off',
      withPickControl: 'Transfers only',
      withoutPickControl: 'No transfer only',
      noMinimum: 'No minimum',
      percent: '{{percent}}%',
      editing: 'Editing',
      editPanel: 'Edit {{name}}',
      sameNote: '"Default" follows your settings for all entries.',
      evMeta: 'EV {{amount}}',
      inputSuffix: '% · {{amount}}',
      percentSuffix: '%',
      emptyTitle: 'No contest settings yet',
      emptyBody:
        'Your settings for all entries apply to every contest. Add a contest to set different rules for it.',
      emptyAction: 'Add a contest',
      unnamed: 'Contest',
      summary: {
        offersOff: 'Offers off',
        offersOn: 'Offers on',
        pickControlOnly: 'Transfers only',
        noPickControl: 'No transfer only',
        min: 'Min {{percent}}%',
        noMinimum: 'No minimum',
        alertsOff: 'Alerts off',
        alertsAt: 'Alerts at {{percent}}%+',
        alertsOn: 'Alerts on',
      },
    },
    addContest: {
      title: 'Add contest',
      label: 'Add a contest',
      search: 'Search your contests',
      intro: 'Your contests',
      group: 'Your contests',
      alreadyAdded: 'Already added',
      custom: 'Custom',
      entryCount__hasPlural: {
        one: '{{count}} entry',
        other: '{{count}} entries',
      },
      emptyTitle: 'No contests yet',
      emptyBody: 'Once you own an entry in a marketplace contest, it shows up here.',
      buyEntries: 'Buy entries',
      noMatch: 'No contests match “{{query}}”',
      clearSearch: 'Clear search',
      loadError: "We couldn't load your contests.",
    },
    contestPage: {
      meta: "{{prize}} · Your entry's EV: <0>{{ev}}</0>",
      evOnly: "Your entry's EV: <0>{{ev}}</0>",
      sameAsAll: 'Default',
      custom: 'Custom',
      customHelper: 'Only for this contest',
      mode: 'Contest settings mode',
    },
  },
};
